import { PencilIcon } from '@heroicons/react/outline';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import useSWR from 'swr';

import { queryParams } from '@shared/commonFunctions';
import { LoadingOverlay } from '@shared/components/grid/overlays/LoadingOverlay';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { Roles } from '@shared/constants/constants';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { SaveButton } from '@shared/inputs/Buttons';
import { NumberInput } from '@shared/inputs/NumberInput';
import { getError } from '@shared/utils/helpers';
import { setLastCommentsRefresh } from '@store/reducers/contactManagerSlice';
import { EditRetaiFormWrapper, EditRetailButtonWrapper } from '@styles/CommonStyles';
import { FormTitle } from '@styles/ContactManager/DealStyles';
import { colorTheme } from '@styles/muiTheme';
import { Flex } from '@styles/StyledDiv';

import { productTypeNames } from '@modules/ContactManager/components/Tabs/shared/DealCalculator/components/DealCalculatorLenders/components/LenderSection/LenderSectionProducts';
import { DealsButton } from '@styles/StyledButtons';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { useQueryState } from '@shared/hooks/useQueryState';
import { ProductStatus } from '../constants';
import { DealProduct, DealProductRate, Deductible, IEditRetailForm, IProductsToVoid, ISelectedRetailToEdit } from '../interfaces';
import { saveProductsPlan, voidAllProduct, voidProduct } from '../queries';
import { NContact } from '../../../../Contact/validation';
import { ISelectedRetailDeductible, ISelectedRetailOption } from './EditProductModal';
import { EditRetailFormRules } from './EditRetailFormRules';
import { EditRetailModal } from './EditRetailModal';
import { useRetailValidation } from './helpers';

interface IRate extends DealProductRate {
  amount: number;
  markup: number;
}
export interface IGeneralProducts {
  productTypeId?: string;
  dealProductId?: string;
  dealCalculatorLeadProductId?: string;
  productTypeName?: string;
  rate: IRate;
  markup: number;
  option?: Deductible;
  status?: string;
  cost?: number;
  retail?: number;
}

export const EditRetailForm = ({
  setChangesMade,
  selectedRetails,
  productRules,
  setSelectedRetails,
  customData,
  setDealProducts,
  dealProducts,
  isVoidEnable,
  reloadDeal,
  setRefresh,
  refresh,
  preventDealSave,
}: IEditRetailForm) => {
  const { isAdmin, userHasRoles } = useUserAccount();

  const isEditProductEnable = (isAdmin || userHasRoles([Roles.EDIT_PRODUCT_PRICE])) && !preventDealSave;

  const [isEditRetailModalOpen, setEditRetailModalOpen] = useState(0);
  const [selectedRetail, setSelectedRetail] = useState<ISelectedRetailToEdit | null>();
  const [isVoidAllVisible, setIsVoidAllVisible] = useState(false);
  const [retailList, setRetailList] = useState<JSX.Element[]>([]);
  const [productsList, setProductsList] = useState<IGeneralProducts[]>([]);
  const [loading, setLoading] = useState(false);
  const [voidLoader, setVoidLoader] = useState(false);
  const [markupApplied, setMarkupApplied] = useState<{ [key: string]: number }>({});
  const [costSumApplied, setCostSumApplied] = useState<{ [key: string]: number }>({});
  const [retailSumApplied, setRetailSumApplied] = useState<{ [key: string]: number }>({});
  const productsToVoid: IProductsToVoid[] = [];
  const [openMarkupAlertModal, setOpenMarkupAlertModal] = useState(false);

  const params = queryParams();
  const dispatch = useDispatch();
  const [contactIdQuery] = useQueryState('id');
  const dealId = params.dealId;
  const getRetailsFormUrl = customData?.getRetailsFormUrl;
  const url = getRetailsFormUrl ? getRetailsFormUrl : `/${SERVICES.PRODUCT}/deals/${dealId}/dealproducts`;
  const { data: dealProduct, mutate: mutateDealProducts, error: dealProductErr, isValidating, isLoading } = useSWR(url);
  const { data: contactData, isLoading: isContactDataLoading } = useSWR<NContact.Entity>(
    contactIdQuery && `/${SERVICES.CONTACT}/contacts/${contactIdQuery}`
  );

  const contactState = contactData?.addresses?.find((address) => address.isCurrent)?.state || '';

  const { isConfigurationLoading, validateProducts, getFlVscMarkupAlertText, isFlVscMarkupInvalid } = useRetailValidation(
    productsList,
    markupApplied
  );

  const [markupAlertText, setMarkupAlertText] = useState(getFlVscMarkupAlertText());

  useEffect(() => {
    if (dealProductErr) {
      Notistack.generalError(getError(dealProductErr));
    }
  }, [dealProductErr]);

  useEffect(() => {
    selectedRetails?.dealsProductsMap.clear();

    if (dealProduct && !isValidating) {
      dealProduct.forEach((product: DealProduct) => {
        selectedRetails?.dealsProductsMap.set(product.productTypeId, product);
      });

      setSelectedRetails({ ...selectedRetails });
      setDealProducts && setDealProducts(dealProduct);
    }
  }, [dealProduct, isValidating]);

  const onVoidClick = useCallback(
    async (product: DealProduct) => {
      setChangesMade(true);
      setVoidLoader(true);
      await voidProduct(product.dealProductId, dealId);
      setVoidLoader(false);
      mutateDealProducts();
      setRefresh(refresh + 1);
    },
    [dealId]
  );

  useEffect(() => {
    const list: JSX.Element[] = [];
    const products: IGeneralProducts[] = [];

    selectedRetails.dealsProductsMap.forEach((product: DealProduct) => {
      products.push(product as unknown as IGeneralProducts);
    });

    selectedRetails.deductiblesSelected.forEach((deductible: ISelectedRetailDeductible) => {
      const index = products.findIndex((product) => {
        return product.productTypeId === deductible.product.productTypeId;
      });
      const data = {
        productTypeId: deductible.product.productTypeId,
        productTypeName: deductible.product.productName,
        rate: deductible.rate,
        markup: deductible.rate.markup,
        option: deductible.option,
        retail: deductible.rate.retail,
      };

      if (index !== -1) {
        products[index] = {
          ...products[index],
          ...data,
          markup: products[index].markup ?? deductible.rate.markup,
        };
      } else {
        products.push(data as IGeneralProducts);
      }
    });

    products?.forEach((product: IGeneralProducts) => {
      const isContracted = product.status === ProductStatus.CONTRACTED;
      const productTypeName = product.productTypeName;
      const productRetailAmountMax = selectedRetails.deductiblesSelected.get(product.productTypeId || '')?.provider.maxAllowedRetail || 0;

      if (isContracted) {
        productsToVoid.push({
          productTypeName: productTypeName,
          dealProductId: product.dealProductId,
        } as IProductsToVoid);
        setIsVoidAllVisible(true);
      }
      const setProduct = selectedRetails.dealsProductsMap.get(product.productTypeId || '');
      const setRetail = setProduct?.retail || 0;
      setRetailSumApplied((prev) => ({ ...prev, [product.productTypeId || '']: retailSum }));
      const setMarkup = setProduct?.markup || 0;
      setMarkupApplied((prev) => ({ ...prev, [product.productTypeId || '']: appliedMarkup }));
      let costSum = (product.rate?.amount ?? 0) + (product.option?.amount ?? 0) || (product?.cost ?? 0);
      setCostSumApplied((prev) => ({ ...prev, [product.productTypeId || '']: costSum }));
      let appliedMarkup = Math.abs(setMarkup) > 0 ? setMarkup : product.markup ?? 0;
      setMarkupApplied((prev) => ({ ...prev, [product.productTypeId || '']: appliedMarkup }));
      if (
        productRetailAmountMax &&
        costSum + (product.markup ?? 0) > productRetailAmountMax &&
        (product.option?.amount ?? 0 > 0)
      ) {
        appliedMarkup -= product.option?.amount ?? 0;
        setMarkupApplied((prev) => ({ ...prev, [product.productTypeId || '']: appliedMarkup }));
      }

      selectedRetails.optionSelected.forEach((opt: ISelectedRetailOption) => {
        if (productTypeName === opt.product.productName) {
          costSum += opt.option.optionAmount;
          setCostSumApplied((prev) => ({ ...prev, [product.productTypeId || '']: costSum }));
        }

        if ((productRetailAmountMax && ((costSum + (product.markup ?? 0)) > productRetailAmountMax)) 
          && (productTypeName === opt.product.productName))
        {
          appliedMarkup -= opt.option.optionAmount;
          setMarkupApplied((prev) => ({ ...prev, [product.productTypeId || '']: appliedMarkup }));
        }
        if (productRetailAmountMax && setRetail > 0 && setRetail > productRetailAmountMax 
          && (productTypeName === opt.product.productName) && (Math.abs(setMarkup) > 0))
        {
          appliedMarkup = setMarkup;
          setMarkupApplied((prev) => ({ ...prev, [product.productTypeId || '']: appliedMarkup }));
        }
      });
      const retailSum = setRetail > 0 
        ? setRetail 
        : (costSum + (product.markup ?? 0)) > productRetailAmountMax
        ? productRetailAmountMax
        : (costSum + (product.markup ?? 0));

      setRetailSumApplied((prev) => ({ ...prev, [product.productTypeId || '']: retailSum }));

      const canEditProduct = productTypeName?.toLowerCase() !== productTypeNames.mbi.toLowerCase();

      list.push(
        <Grid
          key={`key-${product.productTypeId}`}
          container
          item
          spacing={3}
        >
          <Grid
            item
            xs={1}
          >
            <FormTitle>{productTypeName}</FormTitle>
          </Grid>
          <Grid
            item
            xs={2}
          >
            <NumberInput
              disabled
              allowNegative
              decimalScale={2}
              prefix="$"
              value={costSum}
            />
          </Grid>
          <Grid
            item
            xs={2}
          >
            <NumberInput
              disabled
              allowNegative
              decimalScale={2}
              prefix="$"
              value={appliedMarkup}
            />
          </Grid>
          <Grid
            item
            xs={3}
            sx={{ display: 'flex' }}
          >
            <NumberInput
              disabled
              allowNegative
              decimalScale={2}
              prefix="$"
              value={retailSum}
            />
            {!!product.status && canEditProduct && (
              <IconButton
                sx={{ visibility: isContracted || !isEditProductEnable ? 'hidden' : '' }}
                onClick={() => {
                  setSelectedRetail({
                    markup: appliedMarkup,
                    retail: retailSum ?? 0,
                    dealProductId: product.dealProductId ?? '',
                    dealCalculatorLeadProductId: product.dealCalculatorLeadProductId,
                    cost: costSum ?? 0,
                    productTypeName: productTypeName,
                  });
                  setEditRetailModalOpen((prevState) => prevState + 1);
                }}
              >
                <PencilIcon
                  color={colorTheme.palette.primary.dark}
                  width="20px"
                />
              </IconButton>
            )}
          </Grid>

          <Grid
            item
            xs={2}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <FormTitle>{product.status}</FormTitle>
          </Grid>

          {isContracted && isVoidEnable && (
            <Grid
              item
              xs={2}
              sx={{ display: 'flex', justifyContent: 'flex-end' }}
            >
              <DealsButton
                sx={{ alignSelf: 'center' }}
                variant="contained"
                onClick={() => onVoidClick(product as unknown as DealProduct)}
              >
                Void
              </DealsButton>
            </Grid>
          )}
        </Grid>
      );
    });

    if (!productsToVoid.length) {
      setIsVoidAllVisible(false);
    }

    setRetailList(list);
    setProductsList(products);
  }, [selectedRetails]);

  const showLoading = useMemo(
    () =>
      (!dealProduct && !dealProductErr) ||
      isValidating ||
      isLoading ||
      isConfigurationLoading ||
      isContactDataLoading ||
      voidLoader ||
      loading,
    [dealProduct, dealProductErr, isValidating, isLoading, voidLoader, loading, isConfigurationLoading, isContactDataLoading]
  );

  const handleSave = useCallback(async () => {
    if (preventDealSave) return;
    const letsUpdateProducts = validateProducts(contactState);
    if (letsUpdateProducts) {
      for (const [key, product] of selectedRetails.dealsProductsMap.entries()) {
        const productTypeId = product.productTypeId || key;
        if (retailSumApplied[productTypeId] !== undefined) {
          product.retail = retailSumApplied[productTypeId];
        }

        if (markupApplied[productTypeId] !== undefined) {
          product.markup = markupApplied[productTypeId];
        }

        if (costSumApplied[productTypeId] !== undefined) {
          product.cost = costSumApplied[productTypeId];
        }
        if (product.cost - Math.abs(product.markup || 0) >= product.retail) {
          product.maxAllowedRetail = product.retail;
        }
      }

      setChangesMade(true);
      setLoading(true);
      saveProductsPlan(selectedRetails, dealId, customData).then(() => {
      setLoading(false);
      selectedRetails.deductiblesSelected.clear();
      selectedRetails.dealsProductsMap.clear();
      reloadDeal?.();
      mutateDealProducts();
      setRefresh(refresh + 1);
      dispatch(setLastCommentsRefresh(new Date().toISOString()));
      });
    } else {
      setMarkupAlertText(getFlVscMarkupAlertText());
      setOpenMarkupAlertModal(true);
      return;
    }
  }, [
    markupApplied,
    selectedRetails,
    selectedRetails?.dealsProductsMap,
    dealId,
    customData,
    setLastCommentsRefresh,
    reloadDeal,
    refresh,
    contactState,
  ]);

  const onVoidAllClick = useCallback(async () => {
    if (preventDealSave) return;

    setChangesMade(true);
    setVoidLoader(true);
    const voidProducts: IProductsToVoid[] = [];
    selectedRetails.dealsProductsMap.forEach((product: DealProduct) => {
      if (product.status === ProductStatus.CONTRACTED) {
        voidProducts.push({
          productTypeName: product.productTypeName,
          dealProductId: product.dealProductId,
        } as IProductsToVoid);
      }
    });

    await voidAllProduct(dealId, voidProducts);
    dispatch(setLastCommentsRefresh(new Date().toISOString()));
    setVoidLoader(false);
    setRefresh(refresh + 1);
    mutateDealProducts();
  }, [dealId, selectedRetails.dealsProductsMap]);

  return (
    <>
      <EditRetailButtonWrapper>
        <SaveButton
          width="64px"
          label={loading ? 'Saving...' : 'Save'}
          disabled={loading || preventDealSave}
          onClick={handleSave}
        />
      </EditRetailButtonWrapper>
  
    <EditRetaiFormWrapper>
      {showLoading ? (
        <Box sx={{ height: '500px', width: '100%' }}>
          <LoadingOverlay />
        </Box>
      ) : selectedRetails.dealsProductsMap.size === 0 && !selectedRetails.deductiblesSelected.size ? (
        <Typography>Please select a product plan.</Typography>
      ) : (
        <Box>
          <Flex>
            <Grid
              container
              spacing={1}
              alignItems="center"
              justifyContent="center"
            >
              <Grid
                container
                item
                spacing={3}
              >
                <Grid
                  item
                  xs={1}
                ></Grid>
                <Grid
                  item
                  xs={2}
                >
                  <FormTitle>Cost</FormTitle>
                </Grid>
                <Grid
                  item
                  xs={2}
                >
                  <FormTitle>Markup</FormTitle>
                </Grid>
                <Grid
                  item
                  xs={2}
                >
                  <FormTitle>Retail</FormTitle>
                </Grid>
                <Grid
                  item
                  xs={3}
                />
                <Grid
                  item
                  xs={2}
                  sx={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  {isVoidAllVisible && isVoidEnable && (
                    <DealsButton
                      variant="contained"
                      onClick={onVoidAllClick}
                    >
                      Void All
                    </DealsButton>
                  )}
                </Grid>
              </Grid>
              {retailList}
            </Grid>
          </Flex>
        </Box>
      )}
      {!showLoading && !!productRules.length && (
        <EditRetailFormRules
          productRules={productRules}
          dealProducts={dealProducts}
        />
      )}

      <EditRetailModal
        selectedRetail={selectedRetail}
        isEditRetailModalOpen={isEditRetailModalOpen}
        setChangesMade={setChangesMade}
        setEditRetailModalOpen={setEditRetailModalOpen}
        mutateDealProducts={mutateDealProducts}
        customData={customData}
        productRules={productRules}
        {...{ getFlVscMarkupAlertText, isFlVscMarkupInvalid, contactState }}
      />

      {openMarkupAlertModal && (
        <AlertModal
          title="Alert"
          textSize="20px"
          subtitle={markupAlertText}
          buttonText="OK"
          marginTop={0}
          open={openMarkupAlertModal}
          onConfirm={() => {
            setOpenMarkupAlertModal(false);
          }}
          onClose={() => {
            setOpenMarkupAlertModal(false);
          }}
        />
      )}
    </EditRetaiFormWrapper>
    </>
  );
};
